/**
 * Sync of server-resolved (sensitive-tier) feature flags.
 *
 * The backend evaluates region rules (GET /feature-flags/resolved) and returns
 * booleans only — the rules and country lists never reach the client. Values
 * are merged into the flag client with precedence overrides > server > CDN >
 * defaults, and cleared on logout so sensitive keys fall back to false
 * (fail closed).
 *
 * Wiring:
 *   - main.tsx calls installServerFlagsSync() once at boot: initial sync when
 *     a session exists, plus a window-focus re-sync throttled to 60s and an
 *     auth:logout-required listener (the api layer fires it on hard 401s).
 *   - useAuth calls syncServerFlags() after a successful login and
 *     clearServerFlags() on logout.
 */

import { apiService } from '@/services/api';
import { authService } from '@/services/authService';
import { getFeatureFlagClient } from './react';

interface ResolvedFlagsResult {
  flags: Record<string, boolean>;
  region: string;
}

const SYNC_THROTTLE_MS = 60_000;

let lastSyncAt = 0;
let installed = false;

// Whether a resolve attempt for the current session has finished (success or
// failure). Pages that must not render for a disallowed region wait on this,
// so a hard refresh does not bounce an allowed user before the flags arrive.
let resolved = false;
const resolvedListeners = new Set<() => void>();
const setResolved = (value: boolean) => {
  if (resolved === value) return;
  resolved = value;
  resolvedListeners.forEach(listener => listener());
};

export const areServerFlagsResolved = (): boolean => resolved;

export function subscribeServerFlagsResolved(listener: () => void): () => void {
  resolvedListeners.add(listener);
  return () => {
    resolvedListeners.delete(listener);
  };
}

/**
 * Fetch resolved flags for the logged-in user and push them into the flag
 * client. No-op when logged out. Never throws — on failure the client keeps
 * whatever it had (or the fail-closed default).
 */
export async function syncServerFlags(): Promise<void> {
  if (!authService.isAuthenticated()) return;
  lastSyncAt = Date.now();
  try {
    const response = await apiService.get<{ result?: ResolvedFlagsResult }>(
      '/private/api/v1/feature-flags/resolved',
    );
    const flags = response?.result?.flags;
    if (flags && typeof flags === 'object') {
      getFeatureFlagClient().setServerFlags(flags);
    }
  } catch {
    // Fail closed: keep current values; sensitive keys default to false.
  } finally {
    setResolved(true);
  }
}

/** Drop server-resolved values (logout/401) — sensitive keys return to false. */
export function clearServerFlags(): void {
  lastSyncAt = 0;
  setResolved(false);
  try {
    getFeatureFlagClient().setServerFlags(null);
  } catch {
    // Client not registered yet (early logout event) — nothing to clear.
  }
}

/**
 * Install boot-time sync + focus re-sync (throttled) + logout cleanup.
 * Idempotent; call once from main.tsx after registerFeatureFlagClient().
 */
export function installServerFlagsSync(): void {
  if (installed) return;
  installed = true;

  void syncServerFlags();

  window.addEventListener('focus', () => {
    if (Date.now() - lastSyncAt >= SYNC_THROTTLE_MS) {
      void syncServerFlags();
    }
  });

  window.addEventListener('auth:logout-required', () => {
    clearServerFlags();
  });
}

/** Test hook: reset module state between specs. */
export function resetServerFlagsSyncForTests(): void {
  lastSyncAt = 0;
  installed = false;
  resolved = false;
  resolvedListeners.clear();
}
